/**
 * Shadows @plone/volto Footer. Doxcelerate-branded, DOE / energy.gov content
 * removed. Footer link groups come from the active site config.
 *
 * Emits the design system's exact `prc-*` DOM (SiteFooter — CONTRACT D of the
 * faithful design port). The `.prc-footer` CSS lives in the
 * globally-imported ds-core.css; this component owns only the markup + data
 * wiring.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Doxmark, Wordmark } from '../../../../components/Layout/Brand';
import { useSiteConfig } from '../../../../helpers/useSiteConfig';

/** BL-0079: opens the consent banner's Manage panel in place of a full
 *  navigation when JS is present; a plain anchor to #your-choices otherwise,
 *  so crawlers and no-JS clients still reach the notice page. */
const openPrivacyChoices = (event) => {
  if (typeof window !== 'undefined' && window.ppConsent) {
    event.preventDefault();
    window.ppConsent.open();
  }
};

const Footer = () => {
  const site = useSiteConfig();
  const year = new Date().getFullYear();
  return (
    <footer
      id="footer"
      className="prc-footer"
      role="contentinfo"
      aria-label="Footer"
    >
      <div
        className="prc-footer__inner"
        style={{ '--_cols': site.footerGroups.length }}
      >
        <div className="prc-footer__brand">
          <div className="prc-footer__mark">
            <Link to="/" className="prc-footer__logo-link" title={site.title}>
              <Wordmark className="prc-footer__wordmark" />
            </Link>
            {/* Same lockup as the masthead: wordmark over "archives of
                {name}", name linking to /about — the footer should read as
                the same claim repeated, not a different one. */}
            <p className="prc-footer__tagline">
              Archives of{' '}
              <Link to="/about" className="prc-footer__tagline-link">
                {site.title}
              </Link>
            </p>
          </div>
          {site.tagline ? (
            <p className="prc-footer__desc">{site.tagline}</p>
          ) : null}
        </div>
        {site.footerGroups.map((group) => (
          <nav
            className="prc-footer__col"
            key={group.heading}
            aria-label={group.heading}
          >
            {/* h2, not h3 (TAIUIT item 9): these are top level inside
                the footer landmark, and h3 under the page h1 skipped a
                level. ds-core.css styles `.prc-footer__col h3`, so the
                class re-attaches that styling from theme/a11y.less. */}
            <h2 className="prc-footer__col-h">{group.heading}</h2>
            <ul>
              {group.links.map((l) => (
                <li key={l.to}>
                  {l.external ? (
                    <a href={l.to} rel="noopener noreferrer">
                      {l.label}
                    </a>
                  ) : (
                    <Link to={l.to}>{l.label}</Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        ))}
      </div>
      {/* BL-0082: the non-affiliation notice, on every page. It used to
          live only on /about, which is reachable only by clicking the
          masthead tagline -- so nobody arriving from a search result
          ever saw it. Rendered above the copyright line, not inside it,
          because it is a claim about the whole site rather than a legal
          footnote. */}
      <div className="prc-footer__disclaimer">
        <p className="prc-footer__disclaimer-inner">
          {site.disclaimer}{' '}
          <Link to="/about">More about this archive</Link>
        </p>
      </div>
      <div className="prc-footer__legal">
        <div className="prc-footer__legal-inner">
          <span className="prc-logo prc-logo--mark">
            <Doxmark />
          </span>
          <span>© {year} Doxcelerate Corporation. All rights reserved.</span>
          {/* BL-0096. In the legal row rather than a footerGroups column:
              this belongs beside the privacy notice, and giving it a column of
              its own would take the footer from two brand-sized columns to
              three on all three sites for two links. */}
          <Link to="/contact" className="pp-consent-link">
            Contact
          </Link>
          {/* BL-0157. The privacy notice and the opt-out sit in one wrapper
              so the choice control is findable on every page, not only while
              the banner is up. display: contents (theme/consent.less) keeps both
              links as items of the legal row, so nothing moves. */}
          <div className="pp-privacy-choices privacy-notice">
            <Link to="/privacy" className="pp-consent-link">
              Privacy notice
            </Link>
            <a
              href="/privacy#your-choices"
              className="pp-consent-link"
              onClick={openPrivacyChoices}
            >
              Do Not Sell or Share My Personal Information
            </a>
          </div>
          <a
            href="#main"
            className="prc-footer__top"
            style={{ marginLeft: 'auto', color: 'inherit' }}
          >
            ↑ Back to top
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
